import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Truck, CreditCard, Award } from "lucide-react";
import { api, formatPrice, resolveImageUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

// One consistent studio treatment for all 7 - our own catalog photos (real
// products, verified by eye) cut out and composited onto the same dark
// gradient backdrop (see scratchpad/composite.py) so the set reads as one
// shoot instead of 7 photos from 7 different sources/moods. Self-hosted
// under /api/uploads/category/, so this doesn't depend on any third
// party's hotlink policy the way the old Pexels URLs did.
const CATEGORY_BENTO = [
  {
    slug: "iphone",
    title: "iPhone",
    kicker: "Титан. Скорость. Pro.",
    image: "/api/uploads/category/iphone.jpg",
    className: "md:col-span-8 md:row-span-2",
    dark: true,
  },
  {
    slug: "mac",
    title: "Mac",
    kicker: "Магия M-серии.",
    image: "/api/uploads/category/mac.jpg",
    className: "md:col-span-4 md:row-span-1",
    dark: true,
  },
  {
    slug: "watch",
    title: "Watch",
    kicker: "Приключения без границ.",
    image: "/api/uploads/category/watch.jpg",
    className: "md:col-span-4 md:row-span-1",
    dark: true,
  },
  {
    slug: "airpods",
    title: "AirPods",
    kicker: "Звук нового уровня.",
    image: "/api/uploads/category/airpods.jpg",
    className: "md:col-span-6 md:row-span-1",
    dark: true,
  },
  {
    slug: "ipad",
    title: "iPad",
    kicker: "Тонкий. Быстрый. Творческий.",
    image: "/api/uploads/category/ipad.jpg",
    className: "md:col-span-6 md:row-span-1",
    dark: true,
  },
  {
    slug: "dyson",
    title: "Dyson",
    kicker: "Пылесосы. Стайлеры. Климат.",
    image: "/api/uploads/category/dyson.jpg",
    className: "md:col-span-6 md:row-span-1",
    dark: true,
  },
  {
    slug: "accessories",
    title: "Аксессуары",
    kicker: "Клавиатуры. Мыши. Кабели.",
    image: "/api/uploads/category/accessories.jpg",
    className: "md:col-span-6 md:row-span-1",
    dark: true,
  },
];

function pickDiverse(items, count) {
  const byCategory = new Map();
  for (const item of items) {
    const list = byCategory.get(item.category_slug) || [];
    list.push(item);
    byCategory.set(item.category_slug, list);
  }
  const categories = Array.from(byCategory.keys());
  const result = [];
  let round = 0;
  while (result.length < count) {
    const before = result.length;
    for (const cat of categories) {
      const list = byCategory.get(cat);
      if (round < list.length) {
        result.push(list[round]);
        if (result.length >= count) break;
      }
    }
    if (result.length === before) break;
    round += 1;
  }
  return result;
}

function productTileImage(product) {
  if (!product) return null;
  return (
    product.images?.[0] ||
    product.variations?.[0]?.images?.[0] ||
    product.variations?.[0]?.image ||
    null
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    document.title = "vip77.store — Официальная техника Apple";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Официальная техника Apple и премиальная электроника в vip77.store — iPhone, iPad, MacBook и аксессуары.");
  }, []);

  useEffect(() => {
    (async () => {
      const [f, n] = await Promise.all([
        api.get("/catalog/products", { params: { featured: true, limit: 8 } }),
        api.get("/catalog/products", { params: { new: true, sort: "new", limit: 60 } }),
      ]);
      setFeatured(f.data);
      setNewest(n.data);
      setHeroLoading(false);
    })();
  }, []);

  // One curated featured product per category - the iPhone one doubles as
  // the hero "hit of the week" card. TODO: once there's real order history,
  // pick the hero by actual sales instead of the curated is_featured flag.
  const featuredByCategory = useMemo(() => {
    const map = {};
    for (const p of featured) {
      if (!map[p.category_slug]) map[p.category_slug] = p;
    }
    return map;
  }, [featured]);

  const heroProduct = featuredByCategory.iphone || featured[0] || null;

  // "sort=new" ties break by insertion order, and a whole category's import
  // batch can share near-identical created_at timestamps - without this,
  // the top 4-8 "newest" items were all the same iPhone model in different
  // colors. Round-robin across categories instead so the section actually
  // showcases variety.
  const diverseNewest = useMemo(() => pickDiverse(newest, 8), [newest]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 md:grid-cols-12 md:py-24 lg:px-8">
          <div className="md:col-span-6 rise-in">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-muted">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
              В наличии · Гарантия · Оригинал
            </div>
            <h1 className="text-5xl font-extrabold leading-none tracking-tightest text-ink md:text-6xl lg:text-7xl">
              Premium tech.<br />
              <span className="font-light">Прямо сейчас.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted md:text-lg">
              Официальная техника Apple и премиальная электроника в vip77.store. iPhone, MacBook, iPad, Apple Watch, AirPods и Dyson — оригинал с гарантией и доставкой по Москве и всей России.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/catalog/iphone"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft"
              >
                В каталог iPhone <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/catalog/mac"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
              >
                MacBook нового поколения
              </Link>
            </div>
          </div>
          <div className="relative md:col-span-6">
            <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl">
              {heroLoading ? (
                <div className="h-[420px] w-full animate-pulse bg-paper md:h-[520px]" />
              ) : (
                <img
                  src={resolveImageUrl(productTileImage(heroProduct)) || "https://images.pexels.com/photos/18525574/pexels-photo-18525574.jpeg"}
                  alt={heroProduct?.name || "iPhone"}
                  className="h-[420px] w-full bg-white object-contain p-6 md:h-[520px]"
                />
              )}
              {heroProduct && (
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/80 p-4 backdrop-blur-xl">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-soft">Хит недели</div>
                      <div className="truncate text-base font-bold tracking-tight text-ink">{heroProduct.model || heroProduct.name}</div>
                    </div>
                    <Link
                      to={`/product/${heroProduct.slug}`}
                      className="shrink-0 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-ink-soft"
                      data-testid="hero-featured-cta"
                    >
                      от {formatPrice(heroProduct.min_price ?? heroProduct.base_price)}
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute -left-8 -top-8 hidden h-32 w-32 rounded-full bg-accent/10 blur-3xl md:block" />
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-line bg-white py-3 text-[11px] font-bold uppercase tracking-[0.28em] text-muted-soft">
          <div className="flex overflow-hidden">
            <div className="marquee-track flex shrink-0 gap-12 whitespace-nowrap px-6">
              {["iPhone 16 Pro Max · В наличии", "MacBook Pro M3 · –12%", "AirPods Pro 2 USB-C", "Apple Watch Ultra 2", "Гарантия Apple 1 год", "Доставка по РФ", "Оригинал 100%", "iPad Pro M4"].concat(["iPhone 16 Pro Max · В наличии", "MacBook Pro M3 · –12%", "AirPods Pro 2 USB-C", "Apple Watch Ultra 2", "Гарантия Apple 1 год", "Доставка по РФ", "Оригинал 100%", "iPad Pro M4"]).map((t, i) => (
                <span key={i} className="text-ink">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bento categories */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Каталог</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
              Выбирайте по категории
            </h2>
          </div>
          <Link to="/catalog/iphone" className="hidden text-sm font-semibold text-ink underline-offset-4 hover:underline md:inline">
            Смотреть всё →
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:auto-rows-[220px] lg:gap-6">
          {CATEGORY_BENTO.map((c) => (
            <Link
              key={c.slug}
              to={`/catalog/${c.slug}`}
              data-testid={`bento-${c.slug}`}
              className={`group relative overflow-hidden rounded-3xl ${c.className} min-h-[220px] transition-transform duration-500 hover:-translate-y-1`}
            >
              <img src={resolveImageUrl(c.image)} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className={`absolute inset-0 ${c.dark ? "bg-gradient-to-t from-black/70 via-black/20 to-transparent" : "bg-gradient-to-t from-white/70 via-white/10 to-transparent"}`} />
              <div className={`relative flex h-full flex-col justify-end p-6 ${c.dark ? "text-white" : "text-ink"}`}>
                <div className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-80">{c.kicker}</div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <h3 className="text-3xl font-extrabold tracking-tightest md:text-4xl">{c.title}</h3>
                  <span className={`grid h-10 w-10 place-items-center rounded-full ${c.dark ? "bg-white text-ink" : "bg-ink text-white"} transition-transform duration-300 group-hover:rotate-45`}>
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Хиты продаж</div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
                Топ vip77.store
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featured.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* New arrivals */}
      {newest.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Новинки</div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
                Только что приехали
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {diverseNewest.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* Trust badges */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <h2 className="mb-8 text-2xl font-extrabold tracking-tightest text-ink md:text-3xl">
          Оригинальная техника Apple с гарантией и доставкой по России
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { i: Shield, t: "Оригинал 100%", d: "iPhone, MacBook, iPad, Apple Watch и AirPods — только официальные каналы поставки, без копий" },
            { i: Truck, t: "Доставка по РФ", d: "Курьером по Москве от 1 дня, а также в любой регион России" },
            { i: CreditCard, t: "Удобная оплата", d: "Наличными, СБП, картой или в рассрочку через Яндекс Сплит" },
            { i: Award, t: "Гарантия Apple", d: "Официальная гарантия от 1 года на каждый товар" },
          ].map(({ i: Icon, t, d }, idx) => (
            <div key={idx} className="rounded-2xl border border-line bg-white p-6">
              <Icon size={22} className="text-ink" />
              <h3 className="mt-4 text-sm font-bold tracking-tight text-ink">{t}</h3>
              <div className="mt-1 text-xs text-muted">{d}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted">
          vip77.store — интернет-магазин оригинальной техники Apple: iPhone, MacBook, iPad, Apple Watch и AirPods,
          а также пылесосы, стайлеры и климатическая техника Dyson. Каждый товар проходит проверку перед отправкой,
          на технику действует гарантия, а доставка возможна по Москве и всей России.
        </p>
      </section>
    </div>
  );
}
